import { Render, resolveAllData } from "@puckeditor/core"

import { PuckData, siteComponents } from "./index"
import type { Config, Data } from "@puckeditor/core"

/**
 * Server-side render of a published Puck payload, no editor. Used by the
 * storefront renderer (apps/storefront [...site]/page.tsx).
 *
 * resolveAllData(data, config) runs every commerce block's resolveData (the
 * storefront registers its data resolvers before rendering, see
 * lib/resolvers.ts), so product data lands in props before Render. Under the
 * react-server export resolveData executes server-side; in the editor it runs
 * client-side with no resolvers registered, which keeps placeholders.
 *
 * Render resolves to the react-server export of @puckeditor/core under the RSC
 * condition, so the client editor code is never pulled in. Importing Render
 * from the main entry (not /rsc) keeps the import resolvable under the
 * storefront's moduleResolution: node — same import the backend admin uses.
 */
export async function StaticPreview({
  data,
  components,
}: {
  data: PuckData
  /** Overrides on top of siteComponents, e.g. the storefront's client SiteForm. */
  components?: Partial<typeof siteComponents>
}) {
  const config = {
    components: { ...siteComponents, ...components },
  } as unknown as Config
  const resolved = await resolveAllData(
    // SAFETY: published_data comes from the editor that writes SiteBlocks
    // payloads; the Puck Data type is structurally compatible.
    data as unknown as Partial<Data>,
    config as unknown as Config
  )

  return (
    <Render
      config={{ components: { ...siteComponents, ...components } }}
      data={resolved as unknown as Parameters<typeof Render>[0]["data"]}
    />
  )
}
